import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

interface TooltipProps {
  label: string;
  children: React.ReactNode;
  side?: 'top' | 'bottom';
  className?: string;
}

const HOVER_DELAY_MS = 300;

/**
 * Tooltip rendered in a body portal with fixed positioning, so it is never
 * clipped by overflow containers (scrollable lists, cards). Appears after a
 * short hover-intent delay, hides on leave/scroll. Keep the aria-label on the
 * wrapped button itself.
 */
export function Tooltip({ label, children, side = 'top', className }: TooltipProps) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const timerRef = useRef<number | undefined>(undefined);
  const [state, setState] = useState<{ x: number; y: number } | null>(null);

  const hide = () => {
    window.clearTimeout(timerRef.current);
    setState(null);
  };

  const show = () => {
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setState({
        x: rect.left + rect.width / 2,
        y: side === 'top' ? rect.top : rect.bottom,
      });
    }, HOVER_DELAY_MS);
  };

  // Hide on any scroll/resize while visible (position would go stale).
  useEffect(() => {
    if (!state) return;
    const dismiss = () => setState(null);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [state]);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  return (
    <span
      ref={triggerRef}
      className={cn('inline-flex', className)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {state &&
        createPortal(
          <span
            role="tooltip"
            style={{
              left: state.x,
              top: side === 'top' ? state.y - 6 : state.y + 6,
              transform: side === 'top' ? 'translate(-50%, -100%)' : 'translateX(-50%)',
            }}
            className="fixed z-[100] whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-[10px] font-medium text-background shadow-pop animate-scale-in pointer-events-none"
          >
            {label}
          </span>,
          document.body
        )}
    </span>
  );
}
